import { useEffect, useMemo, useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import Page from "./Page";
import EntityList from "./EntityList";
import Input from "../elements/Input";
import Select from "../elements/Select";
import DateField from "../elements/Date";
import Pagination from "../elements/Pagination";
import useData from "../../hooks/useData";
import usePage from "../../hooks/usePage";

const OrdersTransmissionsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { executeAction } = useData();
  const { showMessage } = usePage();

  const clientId = location?.state?.clientId || null;
  const clientName = location?.state?.clientName || "";
  const returnPath = location?.state?.returnPath || "/orders/monitor";
  const returnState = location?.state?.returnState || {};

  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [recreatingFileId, setRecreatingFileId] = useState("");
  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");
  const [pageNumber, setPageNumber] = useState(0);
  const [recordsPerPage, setRecordsPerPage] = useState(50);
  const [filterDraft, setFilterDraft] = useState({
    searchText: "",
    status: "all",
    startDate: null,
    endDate: null,
  });
  const [filterApplied, setFilterApplied] = useState({
    searchText: "",
    status: "all",
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    loadTransmissions();
  }, [clientId]);

  const filteredRecords = useMemo(() => {
    const searchText = String(filterApplied.searchText || "")
      .trim()
      .toLowerCase();

    return records
      .filter((row) => {
        if (
          filterApplied.status !== "all" &&
          row?.status !== filterApplied.status
        ) {
          return false;
        }

        const createdAt = row?.createdAt ? new Date(row.createdAt) : null;
        if (
          filterApplied.startDate &&
          createdAt &&
          createdAt < new Date(filterApplied.startDate)
        ) {
          return false;
        }
        if (
          filterApplied.endDate &&
          createdAt &&
          createdAt > new Date(filterApplied.endDate)
        ) {
          return false;
        }

        if (!searchText) {
          return true;
        }

        const haystack = [
          row?.id,
          row?.orderId,
          row?.submissionId,
          row?.idempotencyKey,
          row?.status,
          row?.lastError,
          row?.routeScopeType,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ");

      });
  }, [records, filterApplied]);

  const getSortValue = (row, fieldName) => {
    switch (fieldName) {
      case "recreateFile":
        return row?.id ? 1 : 0;
      case "createdAt":
      case "lastDispatchedAt": {
        const timestamp = new Date(row?.[fieldName] || 0).getTime();
        return Number.isFinite(timestamp) ? timestamp : 0;
      }
      case "attemptCount": {
        const value = Number(row?.attemptCount);
        return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
      }
      default:
        return row?.[fieldName] ?? "";
    }
  };

  const compareValues = (leftValue, rightValue) => {
    if (typeof leftValue === "number" && typeof rightValue === "number") {
      return leftValue - rightValue;
    }

    return String(leftValue ?? "").localeCompare(String(rightValue ?? ""), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  };

  const sortedRecords = useMemo(() => {
    return [...filteredRecords].sort((left, right) => {
      const result = compareValues(
        getSortValue(left, orderBy),
        getSortValue(right, orderBy),
      );

      if (result !== 0) {
        return order === "asc" ? result : -result;
      }

      if (orderBy !== "createdAt") {
        return (
          new Date(right?.createdAt || 0).getTime() -
          new Date(left?.createdAt || 0).getTime()
        );
      }

      return 0;
    });
  }, [filteredRecords, orderBy, order]);

  const totalCount = sortedRecords.length;
  const pagedRows = useMemo(() => {
    const start = pageNumber * recordsPerPage;
    return sortedRecords.slice(start, start + recordsPerPage);
  }, [sortedRecords, pageNumber, recordsPerPage]);

  const loadTransmissions = async () => {
    if (!clientId) {
      setRecords([]);
      return;
    }

    setLoading(true);
    try {
      const results = await executeAction({
        entityName: "OrderTransmission",
        action: "get",
        filter: { clientId },
      });

      if (results?.isSuccessful && Array.isArray(results.data)) {
        setRecords(results.data);
      } else {
        setRecords([]);
        showMessage(
          results?.message || "Failed to load transmissions",
          "error",
        );
      }
    } catch (err) {
      setRecords([]);
      showMessage(err?.message || "Failed to load transmissions", "error");
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    const normalizedStart = filterDraft.startDate || null;
    const normalizedEnd = filterDraft.endDate || null;

    if (
      normalizedStart &&
      normalizedEnd &&
      new Date(normalizedStart) > new Date(normalizedEnd)
    ) {
      showMessage("From date cannot be after To date", "error");
      return;
    }

    setPageNumber(0);
    setFilterApplied({
      searchText: String(filterDraft.searchText || "").trim(),
      status: filterDraft.status || "all",
      startDate: normalizedStart,
      endDate: normalizedEnd,
    });
  };

  const clearFilters = () => {
    const cleared = {
      searchText: "",
      status: "all",
      startDate: null,
      endDate: null,
    };
    setFilterDraft(cleared);
    setFilterApplied(cleared);
    setPageNumber(0);
  };

  const handleSortChange = (property, nextOrder) => {
    setOrderBy(property);
    setOrder(nextOrder);
  };

  const handleToolbarClick = (name) => {
    if (name === "back") {
      navigate(returnPath, { state: returnState });
      return;
    }

    if (name === "refresh") {
      loadTransmissions();
    }
  };

  const downloadRecreatedFile = async (transmissionId) => {
    if (!transmissionId) {
      showMessage("Transmission ID is required", "error");
      return;
    }

    setRecreatingFileId(String(transmissionId));
    try {
      const results = await executeAction({
        entityName: "OrderTransmission",
        action: "recreateSentFile",
        data: { transmissionId: String(transmissionId) },
      });

      if (!results?.isSuccessful || !results?.data?.content) {
        showMessage(results?.message || "Unable to recreate file", "error");
        return;
      }

      const fileName = results?.data?.fileName || "order_recreated.csv";
      const mimeType = results?.data?.mimeType || "text/csv;charset=utf-8;";
      const blob = new Blob([results.data.content], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.setAttribute("download", fileName);
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      showMessage(err?.message || "Unable to recreate file", "error");
    } finally {
      setRecreatingFileId("");
    }
  };

  const statusItems = [
    { id: "all", name: "All" },
    { id: "queued", name: "Queued" },
    { id: "transmitting", name: "Transmitting" },
    { id: "transmitted", name: "Transmitted" },
    { id: "acknowledged", name: "Acknowledged" },
    { id: "rejected", name: "Rejected" },
    { id: "retry_pending", name: "Retry Pending" },
    { id: "failed_final", name: "Failed Final" },
  ];

  const columns = [
    {
      name: "recreateFile",
      label: "File",
      width: "120px",
      render: (row) => {
        const transmissionId = row?.id || "";
        const isBusy = recreatingFileId === String(transmissionId || "");
        return (
          <Button
            size="small"
            variant="outlined"
            disabled={!transmissionId || isBusy}
            onClick={(event) => {
              event.stopPropagation();
              downloadRecreatedFile(transmissionId);
            }}
          >
            {isBusy ? "Creating..." : "Recreate"}
          </Button>
        );
      },
    },
    {
      name: "status",
      label: "Status",
      width: "130px",
    },
    {
      name: "attemptCount",
      label: "Attempts",
      width: "90px",
      render: (row) => row?.attemptCount ?? 0,
    },
    {
      name: "routeScopeType",
      label: "Route Scope",
      width: "130px",
      render: (row) => row?.routeScopeType || "",
    },
    {
      name: "orderId",
      label: "Order ID",
      width: "220px",
      render: (row) => row?.orderId || "",
    },
    {
      name: "submissionId",
      label: "Submission ID",
      width: "220px",
      render: (row) => row?.submissionId || "",
    },
    {
      name: "idempotencyKey",
      label: "Idempotency",
      width: "300px",
      render: (row) => row?.idempotencyKey || "",
    },
    {
      name: "lastError",
      label: "Last Error",
      width: "360px",
      render: (row) => row?.lastError || "",
    },
    {
      name: "lastDispatchedAt",
      label: "Last Dispatch",
      width: "170px",
      render: (row) =>
        row?.lastDispatchedAt
          ? new Date(row.lastDispatchedAt).toLocaleString()
          : "",
    },
    {
      name: "createdAt",
      label: "Created",
      width: "170px",
      render: (row) =>
        row?.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    },
  ];

  return (
    <Page
      pageName="Order Transmissions"
      entityName={clientName || ""}
      includeToolbar
      onToolItemClick={handleToolbarClick}
      items={[
        { name: "back", label: "Back", iconName: "ArrowBack" },
        { name: "refresh", label: "Refresh", iconName: "Refresh" },
      ]}
    >
      {!clientId ? (
        <Typography>Client context is missing for this page.</Typography>
      ) : (
        <Box>
          <Typography sx={{ mb: 1 }}>
            Track all transmission attempts and delivery state.
          </Typography>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1fr 1fr 1fr auto auto",
              gap: 1,
              alignItems: "end",
              mb: 1,
            }}
          >
            <Input
              label="Search"
              value={filterDraft.searchText}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  searchText: value,
                }))
              }
            />

            <Select
              label="Status"
              value={filterDraft.status}
              items={statusItems}
              onChange={(value) =>
                setFilterDraft((previous) => ({ ...previous, status: value }))
              }
            />

            <DateField
              label="From Date"
              value={filterDraft.startDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({
                  ...previous,
                  startDate: value,
                }))
              }
            />

            <DateField
              label="To Date"
              value={filterDraft.endDate}
              onChange={(value) =>
                setFilterDraft((previous) => ({ ...previous, endDate: value }))
              }
            />

            <Button
              variant="outlined"
              onClick={clearFilters}
            >
              Clear
            </Button>

            <Button
              variant="contained"
              onClick={applyFilters}
            >
              Apply
            </Button>
          </Box>

          <Pagination
            totalCount={totalCount}
            pageNumber={pageNumber}
            recordsPerPage={recordsPerPage}
            onPageChange={(newPage) => setPageNumber(newPage)}
            onRecordsPerPageChange={(newRowsPerPage) => {
              setPageNumber(0);
              setRecordsPerPage(Number(newRowsPerPage));
            }}
            styles={{ marginBottom: "6px" }}
          />

          {loading ? (
            <Typography>Loading transmissions...</Typography>
          ) : (
            <EntityList
              entityList={pagedRows}
              columns={columns}
              tableHeight="calc(100vh - 250px)"
              onSortChange={handleSortChange}
              orderBy={orderBy}
              order={order}
            />
          )}
        </Box>
      )}
    </Page>
  );
};

export default OrdersTransmissionsPage;
